import { useEffect, useState } from 'react';

import {
  Box,
  Button as MuiButton,
  Card,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Drawer from '@/components/wrapped/Drawer';
import {
  CARE_CIRCLE,
  FREQUENCIES,
  firstName,
  type ActionItem,
  type Call,
  type Frequency,
} from '@/cam-admin/control-panel/fixtures';

export type ActionDrawerMode = 'view' | 'create' | 'edit';

// An ISO date from the native picker ('2026-06-14') → the app's display style
// ('Sat 14 Jun'), matching how actions read in the list. Left as-is if it isn't
// a parseable date.
const formatDue = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

// The schedule line for the view header: recurring reads 'Every day · 14:00',
// dated reads 'Due Sat 24 May · 16:00'.
const scheduleMeta = (a: ActionItem) => {
  if (a.repeats) return a.time ? `${a.repeats} · ${a.time}` : a.repeats;
  if (a.due) return a.time ? `Due ${a.due} · ${a.time}` : `Due ${a.due}`;
  return null;
};

/**
 * The action drawer: one right-anchored surface for viewing, creating and
 * editing an action (per the Figma). View is read-only with an Edit / Mark as
 * done footer; create and edit share the form. The action data and the list
 * live in ControlPanel, which renders this over the whole frame.
 */
const ActionDrawer = ({
  open,
  mode,
  action,
  linkedCall,
  onClose,
  onSubmit,
  onEdit,
  onDelete,
  onToggleDone,
  onOpenCall,
}: {
  open: boolean;
  mode: ActionDrawerMode;
  // The action being viewed / edited (undefined when creating).
  action?: ActionItem;
  linkedCall?: Call;
  onClose: () => void;
  onSubmit: (fields: Partial<ActionItem>) => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleDone: () => void;
  onOpenCall: (id: string) => void;
}) => {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [assignee, setAssignee] = useState('');
  const [repeats, setRepeats] = useState(false);
  const [frequency, setFrequency] = useState<Frequency>('Every week');
  const [due, setDue] = useState('');
  const [time, setTime] = useState('');

  // Prefill (edit) or clear (create) whenever the form opens or the target
  // changes. View mode reads straight from `action`, so it needs no local copy.
  useEffect(() => {
    if (!open || mode === 'view') return;
    setTitle(action?.title ?? '');
    setNote(action?.note ?? '');
    setAssignee(action?.assignee ?? '');
    setRepeats(Boolean(action?.repeats));
    setFrequency(action?.repeats ?? 'Every week');
    setDue('');
    setTime(action?.time ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, action?.id]);

  // ── view ──
  if (mode === 'view' && action) {
    const meta = [
      `Created by ${action.createdBy}`,
      action.assignee
        ? `Assigned to ${firstName(action.assignee)}`
        : 'Unassigned',
      scheduleMeta(action),
    ].filter(Boolean) as string[];

    return (
      <Drawer
        open={open}
        onClose={onClose}
        title={action.title}
        subtitle={
          <Stack spacing={0.5}>
            {meta.map((m) => (
              <Typography key={m} variant='body1' color='text.secondary'>
                {m}
              </Typography>
            ))}
          </Stack>
        }
        footer={
          <Stack direction='row' spacing={3}>
            <MuiButton
              variant='outlined'
              color='primary'
              fullWidth
              onClick={onEdit}
            >
              Edit
            </MuiButton>
            <Button
              variant='primary'
              size='md'
              isFullWidth
              ariaLabel={action.done ? 'Mark as not done' : 'Mark as done'}
              onPress={onToggleDone}
            >
              {action.done ? 'Mark as not done' : 'Mark as done'}
            </Button>
          </Stack>
        }
      >
        <Stack spacing={3}>
          {linkedCall && (
            <Card>
              <Stack
                direction='row'
                spacing={2}
                onClick={() => onOpenCall(linkedCall.id)}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 3,
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='subtitle2'>Linked call</Typography>
                  <Typography variant='body1' color='text.secondary'>
                    {linkedCall.date} · {linkedCall.time} · with{' '}
                    {linkedCall.withWho}
                  </Typography>
                </Box>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                  <Icon icon='chevron-right' fontSize={16} />
                </Box>
              </Stack>
            </Card>
          )}

          {action.note && (
            <Card>
              <Box sx={{ p: 3 }}>
                <Typography variant='body1' color='text.secondary'>
                  {action.note}
                </Typography>
              </Box>
            </Card>
          )}
        </Stack>
      </Drawer>
    );
  }

  // ── create / edit ──
  const valid = title.trim().length > 0 && (!repeats || due.trim().length > 0);

  const submit = () =>
    onSubmit({
      title: title.trim(),
      note: note.trim() || undefined,
      assignee: assignee || undefined,
      repeats: repeats ? frequency : undefined,
      // The date field can't round-trip the stored display string ('Thu 28
      // May'), so an untouched field keeps the action's existing date rather
      // than wiping it.
      due: due ? formatDue(due) : action?.due,
      time: time.trim() || undefined,
    });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === 'edit' ? 'Edit action' : 'Create action'}
      subtitle={
        mode === 'edit'
          ? `Edit this action for ${CARE_CIRCLE.recipientFirstName}’s care circle.`
          : `Create an action for members of ${CARE_CIRCLE.recipientFirstName}’s care circle.`
      }
      footer={
        <Stack direction='row' spacing={3}>
          {mode === 'edit' && (
            <MuiButton
              variant='outlined'
              color='error'
              fullWidth
              onClick={onDelete}
            >
              Delete action
            </MuiButton>
          )}
          <Button
            variant='primary'
            size='md'
            isFullWidth
            isDisabled={!valid}
            ariaLabel={mode === 'edit' ? 'Save' : 'Continue'}
            onPress={submit}
          >
            {mode === 'edit' ? 'Save' : 'Continue'}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Title'
          placeholder='e.g. Review your shortlist'
          variant='filled'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
        />

        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Note'
          variant='filled'
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />

        <TextField
          slotProps={{
            inputLabel: { shrink: true },
            select: {
              displayEmpty: true,
              renderValue: (v) =>
                v ? (
                  String(v)
                ) : (
                  <Typography
                    component='span'
                    variant='inherit'
                    color='text.secondary'
                  >
                    Who should complete this task?
                  </Typography>
                ),
            },
          }}
          select
          label='Assignee'
          variant='filled'
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          fullWidth
        >
          <MenuItem value=''>Unassigned</MenuItem>
          {CARE_CIRCLE.carers.map((c) => (
            <MenuItem key={c.name} value={c.name}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>

        {/* recurring toggle, in a Floral White card like the Figma */}
        <Card>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'space-between', p: 3 }}
            direction='row'
            spacing={2}
          >
            <Box>
              <Typography variant='body1'>Repeats</Typography>
              <Typography variant='body2' color='text.secondary'>
                Schedule a recurring action
              </Typography>
            </Box>
            <Switch
              slotProps={{ input: { 'aria-label': 'Repeats' } }}
              checked={repeats}
              onChange={(e) => setRepeats(e.target.checked)}
            />
          </Stack>
        </Card>

        {repeats && (
          <TextField
            slotProps={{ inputLabel: { shrink: true } }}
            select
            label='Frequency'
            variant='filled'
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as Frequency)}
            fullWidth
          >
            {FREQUENCIES.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Stack direction='row' spacing={2}>
          <TextField
            slotProps={{ inputLabel: { shrink: true } }}
            label='Date'
            type='date'
            variant='filled'
            value={due}
            onChange={(e) => setDue(e.target.value)}
            helperText={repeats ? 'Required for recurring actions' : undefined}
            fullWidth
          />
          <TextField
            slotProps={{ inputLabel: { shrink: true } }}
            label='Time'
            type='time'
            variant='filled'
            value={time}
            onChange={(e) => setTime(e.target.value)}
            fullWidth
          />
        </Stack>

        <MuiButton
          variant='outlined'
          color='primary'
          fullWidth
          sx={{ borderStyle: 'dashed' }}
        >
          Upload document
        </MuiButton>
      </Stack>
    </Drawer>
  );
};

export default ActionDrawer;
